import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { 
  Languages, 
  Sun, 
  Moon, 
  Bell, 
  BellOff, 
  Save, 
  AlertTriangle,
  Camera,
  User,
  Calendar,
  Clock,
  MapPin,
  Zap,
  CreditCard,
  Trash2,
  Loader2
} from 'lucide-react';
import { useTheme } from '@/lib/hooks/useTheme';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotifications } from '@/lib/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, updateUserProfile, uploadProfilePhoto, deleteUserAccount } from '@/lib/db';
import { NotificationTest } from '@/components/updates/NotificationTest';
import type { HDType } from '@/lib/types';

const MAX_FILE_SIZE = 500 * 1024; // 500ko
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function Settings() {
  const { toast } = useToast();
  const [theme, setTheme] = useTheme();
  const { language, setLanguage } = useLanguage();
  const { enabled: notificationsEnabled, toggleNotifications } = useNotifications();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [profile, setProfile] = useState<{
    displayName?: string;
    energyType?: HDType;
    birthDate?: string;
    birthTime?: string;
    birthPlace?: string;
    photoUrl?: string;
  } | null>(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/barometre/login');
      return;
    }

    const loadProfile = async () => {
      try {
        const userProfile = await getUserProfile(currentUser.id);
        if (userProfile) {
          setProfile(userProfile);
          if (userProfile.photoUrl) {
            setPhotoPreview(userProfile.photoUrl);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger votre profil.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser, navigate, toast]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast({
        variant: "destructive",
        title: "Format non supporté",
        description: "Veuillez choisir une image au format JPG, PNG ou WebP.",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "destructive",
        title: "Fichier trop volumineux",
        description: "La taille de l'image ne doit pas dépasser 500 Ko.",
      });
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!currentUser?.id || !profile) return;

    setSaving(true);
    try {
      const updates: any = { ...profile };

      if (photoFile) {
        const photoUrl = await uploadProfilePhoto(currentUser.id, photoFile);
        updates.photoUrl = photoUrl;
      }

      await updateUserProfile(currentUser.id, updates);
      
      if (notificationsEnabled) {
        await toggleNotifications();
      }

      toast({
        title: "Paramètres sauvegardés",
        description: "Vos modifications ont été enregistrées avec succès.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    try {
      setDeleting(true);
      await deleteUserAccount(currentUser.id);
      await logout();
      
      toast({
        title: "Compte supprimé",
        description: "Votre compte a été supprimé avec succès.",
      });
      
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le compte. Veuillez réessayer.",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profil Utilisateur */}
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <User className="w-5 h-5" />
              Profil Utilisateur
            </h2>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Photo de profil */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Photo de profil"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <label
                  htmlFor="photo"
                  className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full cursor-pointer hover:bg-purple-700 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                </label>
                <input
                  type="file"
                  id="photo"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
              <p className="text-sm text-gray-500">
                JPG, PNG ou WebP. 500 Ko maximum.
              </p>
            </div>

            {/* Informations de base */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom d'utilisateur
                </label>
                <input
                  type="text"
                  value={profile?.displayName || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type énergétique
                </label>
                <select
                  value={profile?.energyType}
                  onChange={(e) => setProfile(prev => ({ ...prev, energyType: e.target.value as HDType }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="projector">Projector</option>
                  <option value="generator">Generator</option>
                  <option value="manifesting-generator">Manifesting Generator</option>
                  <option value="manifestor">Manifestor</option>
                  <option value="reflector">Reflector</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date de naissance
                  </label>
                  <input
                    type="date"
                    value={profile?.birthDate || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, birthDate: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Heure de naissance
                  </label>
                  <input
                    type="time"
                    value={profile?.birthTime || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, birthTime: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Lieu de naissance
                </label>
                <input
                  type="text"
                  value={profile?.birthPlace || ''}
                  onChange={(e) => setProfile(prev => ({ ...prev, birthPlace: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ville, Pays"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personnalisation */}
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Personnalisation
            </h2>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Thème */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                Thème
              </h3>
              <div className="flex items-center gap-4">
                <Button
                  variant={theme === 'light' ? "default" : "outline"}
                  onClick={() => setTheme('light')}
                  className="flex-1"
                >
                  <Sun className="w-4 h-4 mr-2" />
                  Clair
                </Button>
                <Button
                  variant={theme === 'dark' ? "default" : "outline"}
                  onClick={() => setTheme('dark')}
                  className="flex-1"
                >
                  <Moon className="w-4 h-4 mr-2" />
                  Sombre
                </Button>
              </div>
            </div>

            {/* Langue */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5" />
                Langue
              </h3>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as 'fr' | 'en')}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>

            {/* Notifications */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {notificationsEnabled ? (
                  <Bell className="w-5 h-5" />
                ) : (
                  <BellOff className="w-5 h-5" />
                )}
                Notifications
              </h3>
              <NotificationTest />
            </div>
          </CardContent>
        </Card>

        {/* Abonnement & Confidentialité */}
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Abonnement & Confidentialité
            </h2>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600">
                La gestion des abonnements sera bientôt disponible.
              </p>
            </div>

            {/* Suppression du compte */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Supprimer mon compte
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Cette action est irréversible et supprimera définitivement toutes vos données.
              </p>
              {!showDeleteConfirm ? (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mt-4"
                >
                  Supprimer mon compte
                </Button>
              ) : (
                <div className="space-y-4 mt-4">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-red-800 font-medium">
                      Êtes-vous sûr de vouloir supprimer votre compte ?
                    </p>
                    <ul className="list-disc ml-4 mt-2 text-sm text-red-600 space-y-1">
                      <li>Suppression définitive de votre compte</li>
                      <li>Effacement de tout votre historique de diagnostics</li>
                      <li>Suppression de vos données personnelles</li>
                      <li>Action irréversible</li>
                    </ul>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deleting}
                      className="flex-1"
                    >
                      {deleting ? 'Suppression...' : 'Confirmer la suppression'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bouton de sauvegarde */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#54075B] hover:bg-[#54075B]/90 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Sauvegarder les modifications
            </>
          )}
        </Button>
      </div>
    </div>
  );
}